import { forwardRef, useState, useRef, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface CurrencyInputProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value: number;  // Value in dollars (not cents)
  onChange: (value: number) => void;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onChange, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    const lastExternalValue = useRef<number>(value);

    // Only sync from external value when:
    // 1. Not focused (user isn't typing)
    // 2. External value actually changed (not from our own onChange)
    useEffect(() => {
      if (!isFocused && value !== lastExternalValue.current) {
        lastExternalValue.current = value;
        if (value > 0) {
          setDisplayValue(value.toFixed(2));
        } else {
          setDisplayValue('');
        }
      }
    }, [value, isFocused]);

    // Initialize display value on mount
    useEffect(() => {
      if (value > 0) {
        setDisplayValue(value.toFixed(2));
      }
      lastExternalValue.current = value;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Allow empty input
      if (input === '') {
        setDisplayValue('');
        lastExternalValue.current = 0;
        onChange(0);
        return;
      }

      // Remove non-numeric characters except decimal
      let sanitized = input.replace(/[^0-9.]/g, '');

      // Handle multiple decimal points - keep only first
      const decimalIndex = sanitized.indexOf('.');
      if (decimalIndex !== -1) {
        sanitized =
          sanitized.slice(0, decimalIndex + 1) +
          sanitized.slice(decimalIndex + 1).replace(/\./g, '');
      }

      // Limit to 2 decimal places
      const parts = sanitized.split('.');
      if (parts[1] && parts[1].length > 2) {
        sanitized = parts[0] + '.' + parts[1].slice(0, 2);
      }

      setDisplayValue(sanitized);
      const numValue = parseFloat(sanitized) || 0;
      lastExternalValue.current = numValue;
      onChange(numValue);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Format display value on blur
      const numValue = parseFloat(displayValue) || 0;
      if (numValue > 0) {
        setDisplayValue(numValue.toFixed(2));
      } else {
        setDisplayValue('');
      }
    };

    return (
      <TextField
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="0.00"
        inputProps={{
          style: { minWidth: '90px', textAlign: 'right' },
          ...props.inputProps,
        }}
        InputProps={{
          ...props.InputProps,
        }}
      />
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;
